import { JsonRpcSigner, TransactionReceipt as EthersTransactionReceipt, TransactionRequest } from "ethers";

import { switchChain, sendTransaction as _sendTransaction } from "@shared/utils/signerUtils";
import type { TransactionReceipt, Address } from "@shared/types";

import type {
    SignMessage,
    SendTransaction,
    SignTypedData,
    EvmWalletRole,
    UserWalletFunderDisplay,
} from "@halliday-sdk/payments";

/**
 * Get the transaction receipt from the ethers transaction receipt.
 *
 * @param {EthersTransactionReceipt} txReceipt The ethers transaction receipt.
 * @returns {TransactionReceipt} The transaction receipt.
 */
const _getTransactionReceipt = (txReceipt: EthersTransactionReceipt): TransactionReceipt => {
    return {
        transactionHash: txReceipt.hash,
        blockHash: txReceipt.blockHash,
        blockNumber: txReceipt.blockNumber,
        from: txReceipt.from as `0x${string}`,
        to: txReceipt.to as `0x${string}` | undefined,
        rawReceipt: txReceipt,
    };
};

export function connectSigner(
    getSigner: (address?: Address) => Promise<JsonRpcSigner>,
    ownerFunderDisplay: "HIDE",
): Omit<EvmWalletRole, "sendTransaction">;

export function connectSigner(
    getSigner: (address?: Address) => Promise<JsonRpcSigner>,
    ownerFunderDisplay?: "SHOW",
): EvmWalletRole;

/**
 * Connect to a signer.
 *
 * @param {function} getSigner {function} The function to get the signer.
 * @returns {function} ``getAddress`` {function} Function that returns the address of the signer.
 * @returns {function} ``signMessage`` {{@link SignMessage}} Function that signs a message.
 * @returns {function} ``signTypedData`` {{@link SignTypedData}} Function that signs typed data.
 * @returns {function} ``sendTransaction`` {{@link SendTransaction}} Function that sends an EVM transaction.
 * @returns {string} ``walletType`` The literal `"EVM"`.
 */
export function connectSigner(
    getSigner: (address?: Address) => Promise<JsonRpcSigner>,
    ownerFunderDisplay?: UserWalletFunderDisplay,
) {
    const getAddress = async (): Promise<Address> => {
        const signer = await getSigner();
        return signer.getAddress();
    };

    const signMessage: SignMessage = async ({ message, ownerAddress }) => {
        const signer = await getSigner(ownerAddress);
        if (ownerAddress) {
            const address = await signer.getAddress();
            if (ownerAddress !== address) {
                throw new Error("Owner address does not match");
            }
        }
        const signature = await signer.signMessage(message);
        return signature;
    };

    const sendTransaction: SendTransaction = async (transaction, chainConfig) => {
        let signer = await getSigner();
        const signerNetwork = await signer.provider?.getNetwork();
        if (signerNetwork?.chainId?.toString() !== transaction.chainId.toString()) {
            await switchChain(transaction.chainId, signer, chainConfig);
            signer = await getSigner();
        }
        const ethersTxReceipt = await _sendTransaction({ signer, transaction: transaction as TransactionRequest });
        return _getTransactionReceipt(ethersTxReceipt);
    };

    const signTypedData: SignTypedData = async ({ typedData, ownerAddress, chainId }) => {
        let signer = await getSigner(ownerAddress);
        if (ownerAddress) {
            const address = await signer.getAddress();
            if (ownerAddress !== address) {
                throw new Error("Owner address does not match");
            }
        }
        if (chainId !== undefined) {
            const signerNetwork = await signer.provider?.getNetwork();
            if (signerNetwork?.chainId?.toString() !== chainId.toString()) {
                await switchChain(chainId, signer);
                signer = await getSigner(ownerAddress);
            }
        }
        return await signer.provider?.send("eth_signTypedData_v4", [ownerAddress, typedData]);
    };

    if (ownerFunderDisplay === "HIDE") {
        return {
            getAddress,
            signMessage,
            signTypedData,
            walletType: "EVM" as const,
        };
    }

    return {
        getAddress,
        signMessage,
        sendTransaction,
        signTypedData,
        walletType: "EVM" as const,
    };
}
